import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get } from 'lodash';

import { Scrollbars } from 'react-custom-scrollbars';

import { Field, FieldArray } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';

import Tooltip from 'components/common/Tooltip';
import AutosuggestField from 'components/fields/AutosuggestField';
import FiltersFieldArray from 'components/fields/FiltersFieldArray';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

import * as fromFieldActions from 'redux/modules/formControl';

const styles = theme => ({
  autosuggestContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    position: 'relative',
    padding: '1px 4px 2px 4px',
    border: `1px solid ${theme.palette.grey[600]}`,
    borderRadius: 4,
  },
  autosuggestContainerSizeXs: {
    padding: '1px 1px 2px 6px',
  },
  autosuggestContainerColorDark: {
    backgroundColor: theme.palette.grey[100],
  },
  autosuggestContainerColorLight: {
    backgroundColor: theme.palette.common.white,
  },
  autosuggestContainerActive: {
    borderColor: theme.palette.primary[500],
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
  },
  autosuggestContainerTypeSearch: {
    border: 'none',
  },
  textFieldContainer: {
    position: 'relative',
    display: 'inline-flex',
    margin: '8px 4px',
  },
  textFieldContainerSizeXs: {
    margin: '4px 4px 5px 4px',
  },
  textFieldValue: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5, 14),
    lineHeight: pxToEm(16, 14),
    height: pxToEm(16, 14),
    padding: '0 8px 0 0',
    fontFamily: theme.typography.fontFamily,
    opacity: 0,
  },
  textFieldValueTypeSearch: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.5, 16),
  },
  textField: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: 'hidden',
  },
  textFieldInputRoot: {
    border: 'none',
    backgroundColor: 'transparent',
  },
  textFieldInputInput: {
    padding: 0,
  },
  textFieldInputInputTypeSearch: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.5, 16),
  },
  label: {
    position: 'relative',
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: 700,
    color: theme.palette.grey[600],
    transform: 'none',
  },
  labelOverlap: {
    marginBottom: -6,
    marginLeft: 4,
    width: 'fit-content',
    zIndex: 2,
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      right: -1,
      left: -1,
      bottom: 5,
      height: 2,
      backgroundColor: theme.palette.common.white,
      zIndex: -1,
    },
  },
  tooltip: {
    display: 'block',
  },
  autosuggestContainerError: {
    borderColor: theme.palette.validation[500],
  },
  autosuggestContainerNoWrap: {
    overflow: 'hidden',
  },
  autosuggestContainerDisabled: {
    backgroundColor: theme.palette.grey[100],
  },
});

class MultiselectAutosuggestField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      autosuggestSearchInputValue: '',
      hovered: false,
    };

    this.onAutosuggestContainerClick = this.onAutosuggestContainerClick.bind(this);
    this.onAutosuggestSearchInputChange = this.onAutosuggestSearchInputChange.bind(this);
    this.onRenderInputContent = this.onRenderInputContent.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  onAutosuggestContainerClick() {
    if (this.textFieldRef) {
      this.textFieldRef.focus();
    }
  }

  onAutosuggestSearchInputChange(value) {
    this.setState({ autosuggestSearchInputValue: value });
    this.props.onSearchInputChange();
  }

  onSuggestionSelected(...args) {
    this.setState({ autosuggestSearchInputValue: '' });
    return this.props.onSuggestionSelected(...args);
  }

  onRenderInputContent(...args) {
    const { renderInputContent } = this.props;
    const newAutosuggestSearchInputValue = renderInputContent(...args);
    this.setState({ autosuggestSearchInputValue: newAutosuggestSearchInputValue });
    return newAutosuggestSearchInputValue;
  }

  onMouseOver() {
    this.setState({ hovered: true });
  }

  onMouseOut() {
    this.setState({ hovered: false });
  }

  onFocus() {
    const { autosuggestFieldName, onInputFocus, ...otherProps } = this.props;
    const autosuggestField = get(otherProps, autosuggestFieldName);
    onInputFocus();
    if (autosuggestField.meta.touched
      && Boolean(autosuggestField.meta.error)) {
      this.props.actions.focusFormControl(autosuggestField.input.name);
    }
  }

  onBlur() {
    const { onInputBlur } = this.props;
    onInputBlur();
    this.props.actions.blurFormControl();
  }

  render() {
    const {
      label,
      fetchSuggestions,
      renderSuggestionContent,
      renderInputContent,
      autosuggestFieldName,
      fieldArrayName,
      placeholder,
      errorPlacement,
      formControl,
      classes,
      firstHighlighted,
      draggable,
      color,
      textFieldRef,
      maxArrayLength,
      size,
      onFieldArrayItemDelete,
      type,
      labelOverlap,
      noWrap,
      ...otherProps
    } = this.props;
    const { autosuggestSearchInputValue, hovered } = this.state;
    const autosuggestField = get(otherProps, autosuggestFieldName);
    const fieldArray = get(otherProps, fieldArrayName);
    const errorVisible =
      Boolean(autosuggestField.meta.touched)
      && Boolean(autosuggestField.meta.error)
      && (Boolean(autosuggestField.meta.active)
        || (hovered && !formControl));
    let disabled = otherProps.disabled;
    if (maxArrayLength !== null && fieldArray.input.value.length >= maxArrayLength) {
      disabled = true;
    }
    return (
      <FormControl
        fullWidth
        error={(
          Boolean(autosuggestField.meta.touched)
            && Boolean(autosuggestField.meta.error)
        )}
      >
        {label &&
          <InputLabel
            className={classNames(classes.label, {
              [classes.labelOverlap]: labelOverlap === true,
            })}
            disableAnimation
            htmlFor={autosuggestFieldName}
            shrink={false}
          >
            {label}
          </InputLabel>
        }
        <Field
          id={autosuggestFieldName}
          name={autosuggestFieldName}
          component={AutosuggestField}
          multiselectAutosuggestContainerRef={this.multiselectAutosuggestContainerRef}
          renderTextField={(textField, focused) => {
            const selectedFieldArray = (
              <FieldArray
                id={fieldArrayName}
                name={fieldArrayName}
                component={FiltersFieldArray}
                size={size}
                firstHighlighted={firstHighlighted}
                draggable={draggable}
                disabled={otherProps.disabled}
                noWrap={noWrap}
                {...!disabled ? {
                  endAdornment: (
                    <div
                    className={classNames(classes.textFieldContainer, {
                      [classes.textFieldContainerSizeXs]: size === 'xs',
                    })}
                  >
                    <div
                      className={classNames(classes.textFieldValue, {
                        [classes.textFieldValueTypeSearch]: type === 'search',
                      })}
                    >
                      {!autosuggestSearchInputValue
                        && placeholder
                        && fieldArray.input.value.length === 0
                        ? placeholder : autosuggestSearchInputValue}
                    </div>
                    {textField}
                  </div>
                  ),
                } : {}}
                onFieldDelete={(index) => {
                  onFieldArrayItemDelete(index);
                  autosuggestField.input.onBlur();
                }}
              />
            );
            return (
              <Tooltip
                className={classes.tooltip}
                content={
                  (autosuggestField.meta.error) ? (
                    <Typography variant="subheading" color="secondary">
                      {autosuggestField.meta.error}
                    </Typography>
                  ) : ''
                }
                open={errorVisible}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                color="white"
                placement={errorPlacement}
              >
                <div
                  className={classNames(classes.autosuggestContainer, {
                    [classes.autosuggestContainerSizeXs]: size === 'xs',
                    [classes.autosuggestContainerColorLight]: color === 'light',
                    [classes.autosuggestContainerColorDark]: color === 'dark',
                    [classes.autosuggestContainerActive]: focused,
                    [classes.autosuggestContainerNoWrap]: noWrap,
                    [classes.autosuggestContainerError]: (
                      Boolean(autosuggestField.meta.touched)
                        && Boolean(autosuggestField.meta.error)),
                    [classes.autosuggestContainerTypeSearch]: type === 'search',
                    [classes.autosuggestContainerDisabled]: disabled,
                  })}
                  onClick={this.onAutosuggestContainerClick}
                  onMouseOver={this.onMouseOver}
                  onMouseOut={this.onMouseOut}
                  role="presentation"
                  ref={(multiselectAutosuggestContainerRef) => {
                    this.multiselectAutosuggestContainerRef = multiselectAutosuggestContainerRef;
                  }}
                >
                  {noWrap ? (
                    <Scrollbars
                      autoHeight
                      thumbMinSize={30}
                      universal
                    >
                      {selectedFieldArray}
                    </Scrollbars>
                  ) : selectedFieldArray }    
                </div>
              </Tooltip>
            );
          }}
          classes={{
            textField: classes.textField,
            textFieldInputRoot: classes.textFieldInputRoot,
            textFieldInputInput: classNames(classes.textFieldInputInput, {
              [classes.textFieldInputInputTypeSearch]: type === 'search',
            }),
          }}
          endAdornmentClearButton={false}
          fullWidth
          renderSuggestionContent={renderSuggestionContent}
          fetchSuggestions={({ ...args }) =>
            fetchSuggestions({
              ...args,
              fieldArrayValue: fieldArray.input.value,
            })}
          onlyConfirmedSelection
          {...otherProps}
          disabled={disabled}
          textFieldRef={(autoSuggestTextFieldRef) => {
            this.textFieldRef = autoSuggestTextFieldRef;
            textFieldRef(autoSuggestTextFieldRef);
          }}
          renderInputContent={this.onRenderInputContent}
          onSearchInputChange={this.onAutosuggestSearchInputChange}
          placeholder={fieldArray.input.value.length === 0 ? placeholder : ''}
          onSuggestionSelected={this.onSuggestionSelected}
          meta={{
            ...autosuggestField.meta,
            error: null,
          }}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
        />
      </FormControl>
    );
  }
}

MultiselectAutosuggestField.propTypes = {
  label: PropTypes.node,
  placeholder: PropTypes.string,
  fetchSuggestions: PropTypes.func.isRequired,
  renderSuggestionContent: PropTypes.func.isRequired,
  renderInputContent: PropTypes.func.isRequired,
  onSuggestionSelected: PropTypes.func.isRequired,
  autosuggestFieldName: PropTypes.string.isRequired,
  fieldArrayName: PropTypes.string.isRequired,
  errorPlacement: PropTypes.oneOf(['right', 'left', 'top', 'bottom']),
  size: PropTypes.oneOf(['xs', 'sm', 'md']),
  formControl: PropTypes.string,
  actions: PropTypes.shape({
    focusFormControl: PropTypes.func.isRequired,
    blurFormControl: PropTypes.func.isRequired,
  }).isRequired,
  firstHighlighted: PropTypes.bool,
  draggable: PropTypes.bool,
  color: PropTypes.oneOf(['light', 'dark']),
  textFieldRef: PropTypes.func,
  onSearchInputChange: PropTypes.func,
  onInputFocus: PropTypes.func,
  onInputBlur: PropTypes.func,
  onFieldArrayItemDelete: PropTypes.func,
  disabled: PropTypes.bool,
  maxArrayLength: PropTypes.number,
  noWrap: PropTypes.bool,
  classes: PropTypes.shape({
    autosuggestContainer: PropTypes.string.isRequired,
    autosuggestContainerColorDark: PropTypes.string.isRequired,
    autosuggestContainerColorLight: PropTypes.string.isRequired,
    autosuggestContainerActive: PropTypes.string.isRequired,
    textFieldContainer: PropTypes.string.isRequired,
    textFieldValue: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    textField: PropTypes.string.isRequired,
    textFieldInputRoot: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    labelOverlap: PropTypes.string.isRequired,
    autosuggestContainerError: PropTypes.string.isRequired,
  }).isRequired,
};

MultiselectAutosuggestField.defaultProps = {
  label: '',
  placeholder: '',
  errorPlacement: 'right',
  formControl: null,
  firstHighlighted: false,
  draggable: false,
  color: 'light',
  textFieldRef: () => {},
  onSearchInputChange: () => {},
  onInputFocus: () => {},
  onInputBlur: () => {},
  onFieldArrayItemDelete: () => {},
  size: 'sm',
  disabled: false,
  maxArrayLength: null,
  noWrap: false,
};

function mapStateToProps(state) {
  return {
    formControl: state.formControl,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(fromFieldActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(MultiselectAutosuggestField),
);



// WEBPACK FOOTER //
// ./src/components/fields/MultiselectAutosuggestField.js